/**
 * UI Helpers Module
 * Helper functions for UI state updates, auth errors, and connection indicators
 */

import type { State } from './types';
import { DEFAULT_USER_NAME, FALLBACK_ACCOUNT_NAME } from './modules/config';
import { VIEW_MODES } from './modules/state';

interface ElementRefs {
  userName?: HTMLElement | null;
  userPill?: HTMLElement | null;
  mobileMenuUserName?: HTMLElement | null;
  mobileMenuUserInfo?: HTMLElement | null;
  mobileOpenOptions?: HTMLElement | null;
  authError?: HTMLElement | null;
  modeBanner?: HTMLElement | null;
  modeBannerLabel?: HTMLElement | null;
  viewModeControls?: HTMLElement | null;
  viewModeToggle?: HTMLElement | null;
  viewModeToggleLabel?: HTMLElement | null;
  mobileViewAllLots?: HTMLElement | null;
  mobileViewAllLotsLabel?: HTMLElement | null;
  superadminUserSelector?: HTMLElement | null;
  superadminUserCredits?: HTMLElement | null;
}

/**
 * Render user identity in UI elements
 */
export function renderUserIdentity(state: State, els: ElementRefs): void {
  const user = state.user;
  const identityLabel =
    user?.displayName || user?.name || user?.email || user?.company || FALLBACK_ACCOUNT_NAME;
  const isLoggedIn = Boolean(user);
  const displayName = isLoggedIn ? identityLabel : DEFAULT_USER_NAME;

  if (els.userName) {
    els.userName.textContent = displayName;
  }

  if (els.userPill) {
    els.userPill.hidden = !isLoggedIn;
  }

  if (els.mobileMenuUserName) {
    els.mobileMenuUserName.textContent = displayName;
  }

  if (els.mobileMenuUserInfo) {
    els.mobileMenuUserInfo.hidden = !isLoggedIn;
  }

  if (els.mobileOpenOptions) {
    els.mobileOpenOptions.hidden = !isLoggedIn;
  }
}

/**
 * Show authentication error message
 */
export function showAuthError(els: ElementRefs, message: string): void {
  if (!els.authError) return;
  els.authError.textContent = message;
  els.authError.hidden = false;
}

/**
 * Hide authentication error message
 */
export function hideAuthError(els: ElementRefs): void {
  if (!els.authError) return;
  els.authError.textContent = '';
  els.authError.hidden = true;
}

/**
 * Update connection mode indicator banner
 */
export function updateConnectionIndicator(
  els: ElementRefs,
  stateKey: string,
  label: string,
  title: string
): void {
  if (!els.modeBanner) return;
  els.modeBanner.hidden = false;
  (els.modeBanner as HTMLElement).dataset.state = stateKey;
  if (els.modeBannerLabel) {
    els.modeBannerLabel.textContent = label;
  }
  els.modeBanner.setAttribute('aria-label', label);
  if (title) {
    els.modeBanner.title = title;
  } else {
    els.modeBanner.removeAttribute('title');
  }
}

/**
 * Update view mode toggle button state (user vs superadmin view)
 */
export function updateViewModeToggle(
  state: State,
  els: ElementRefs,
  lastRenderState: {
    viewMode?: string;
    viewModeHidden?: boolean;
    viewModeLabel?: string;
    mobileViewModeLabel?: string;
    viewModeAria?: string;
    viewModeActive?: string;
  }
): void {
  if (!state || !els.viewModeControls || !els.viewModeToggle) {
    return;
  }
  const isSuperUser = !!(state.user && state.user.isSuperUser);
  const shouldHide = !isSuperUser;
  if (lastRenderState.viewModeHidden !== shouldHide) {
    els.viewModeControls.hidden = shouldHide;
    lastRenderState.viewModeHidden = shouldHide;
  }
  if (shouldHide) {
    return;
  }
  const isGlobal = state.viewMode === VIEW_MODES.SUPERADMIN;
  // Hide/show children before showing parent to prevent flash
  if (els.superadminUserSelector) {
    els.superadminUserSelector.hidden = !isGlobal;
  }
  if (els.superadminUserCredits) {
    els.superadminUserCredits.hidden = !isGlobal;
  }
  if (els.mobileViewAllLots) {
    els.mobileViewAllLots.hidden = !isSuperUser;
  }
  els.viewModeControls.hidden = false;
  const button = els.viewModeToggle;
  const labelText = isGlobal ? 'View my lots' : 'View all lots';
  if (els.viewModeToggleLabel && lastRenderState.viewModeLabel !== labelText) {
    els.viewModeToggleLabel.textContent = labelText;
    lastRenderState.viewModeLabel = labelText;
  }
  if (els.mobileViewAllLotsLabel && lastRenderState.mobileViewModeLabel !== labelText) {
    els.mobileViewAllLotsLabel.textContent = labelText;
    lastRenderState.mobileViewModeLabel = labelText;
  }
  const ariaLabel = isGlobal
    ? 'Viewing all lots. Activate to switch to viewing only my lots.'
    : 'Viewing my lots. Activate to switch to viewing all lots.';
  if (lastRenderState.viewModeAria !== ariaLabel) {
    button.setAttribute('aria-label', ariaLabel);
    button.setAttribute('title', ariaLabel);
    lastRenderState.viewModeAria = ariaLabel;
  }
  const activeValue = isGlobal ? 'true' : 'false';
  if (lastRenderState.viewModeActive !== activeValue) {
    button.setAttribute('data-active', activeValue);
    button.setAttribute('aria-pressed', activeValue);
    lastRenderState.viewModeActive = activeValue;
  }
}
